import { styled } from '@mui/material';
import type { ReactNode } from 'react';

/** Width of the list column once list and detail sit side by side. */
export const LIST_DETAIL_LIST_WIDTH = 360;

type Props = {
  /** Master column, typically a `ListPanel` of items to pick from. */
  list: ReactNode;
  /** Detail column showing the picked item, typically a `ListPanel` with a `ListPanelHeader`. */
  detail: ReactNode;
  /**
   * Below the `md` breakpoint only one column fits: `true` shows the detail, `false` the list.
   * From `md` both columns are always shown.
   */
  showDetail: boolean;
  'data-testid'?: string;
};

type OwnerState = {
  showDetail: boolean;
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    flex: 1,
    minHeight: 0,
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
}));

const ListPane = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: ownerState.showDetail ? 'none' : 'flex',
  flexDirection: 'column',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    flex: 1,
    minHeight: 0,
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flex: `0 0 ${LIST_DETAIL_LIST_WIDTH}px`,
  },
}));

const DetailPane = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: ownerState.showDetail ? 'flex' : 'none',
  flexDirection: 'column',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    flex: 1,
    minHeight: 0,
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

/**
 * Master / detail arrangement of two panels inside a `fixedHeader` page: a fixed-width list
 * column next to the detail from `md`, one column at a time below (drill-down), switched with
 * `showDetail`. Each column fills the page height from `sm` so its `ListPanel fill` scrolls inside.
 */
export function ListDetailLayout(props: Props) {
  const ownerState = { showDetail: props.showDetail };

  return (
    <Root data-testid={props['data-testid'] ?? 'list-detail-layout'}>
      <ListPane ownerState={ownerState}>{props.list}</ListPane>
      <DetailPane ownerState={ownerState}>{props.detail}</DetailPane>
    </Root>
  );
}

export type { Props as ListDetailLayoutProps };
